import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, Typography, TextField, Button, Grid, Paper, 
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, 
  Pagination, CircularProgress, Alert, Snackbar, IconButton,
  Divider, alpha, useTheme, Fade
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  LocationOn as LocationOnIcon, 
  Search as SearchIcon, 
  Add as AddIcon,
  Refresh as RefreshIcon,
  Public as PublicIcon
} from '@mui/icons-material';
import axios from 'axios';
import API_BASE_URL from '../config';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  background: `linear-gradient(to bottom right, ${alpha(theme.palette.background.paper, 0.9)}, ${alpha(theme.palette.background.paper, 0.95)})`,
  backdropFilter: 'blur(10px)',
  boxShadow: `0 8px 32px 0 ${alpha(theme.palette.common.black, 0.08)}`,
  marginBottom: theme.spacing(4),
}));

const AdminAddressManagement = () => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    state_name: '', state_code: '', district_name: '', district_code: '',
    subdistrict_name: '', subdistrict_code: '', village_name: '', village_code: '', pincode: ''
  });
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [limit] = useState(10);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchAddresses = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/admin/address`, {
        params: { page, limit, search },
        withCredentials: true
      });
      if (res.data.success) {
        setAddresses(res.data.data);
        setTotalPages(res.data.totalPages);
      }
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: 'Failed to fetch addresses', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, limit, search]);

  useEffect(() => {
    fetchAddresses();
  }, [fetchAddresses]);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/admin/address`, formData, { withCredentials: true });
      if (res.data.success) {
        setSnackbar({ open: true, message: 'Address added successfully!', severity: 'success' });
        setFormData({ state_name: '', state_code: '', district_name: '', district_code: '', subdistrict_name: '', subdistrict_code: '', village_name: '', village_code: '', pincode: '' });
        fetchAddresses();
      }
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to add address', severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Fade in timeout={1000}>
        <Box>
          <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
            <PublicIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            <Typography variant="h4" sx={{ fontWeight: 700, background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`, backgroundClip: 'text', WebkitBackgroundClip: 'text', color: 'transparent' }}>
              Address Management
            </Typography>
          </Box>

          <StyledPaper elevation={0}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>Add New Location</Typography>
            <Box component="form" onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField fullWidth label="State Name" name="state_name" value={formData.state_name} onChange={handleInputChange} required size="small" />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField fullWidth label="State Code" name="state_code" type="number" value={formData.state_code} onChange={handleInputChange} required size="small" />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField fullWidth label="District Name" name="district_name" value={formData.district_name} onChange={handleInputChange} required size="small" />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField fullWidth label="District Code" name="district_code" type="number" value={formData.district_code} onChange={handleInputChange} required size="small" />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField fullWidth label="Subdistrict Name" name="subdistrict_name" value={formData.subdistrict_name} onChange={handleInputChange} required size="small" />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField fullWidth label="Subdistrict Code" name="subdistrict_code" type="number" value={formData.subdistrict_code} onChange={handleInputChange} required size="small" />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField fullWidth label="Village Name" name="village_name" value={formData.village_name} onChange={handleInputChange} required size="small" />
                </Grid>
                <Grid item xs={12} sm={6} md={1}>
                  <TextField fullWidth label="Village Code" name="village_code" type="number" value={formData.village_code} onChange={handleInputChange} required size="small" />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField fullWidth label="Pincode" name="pincode" value={formData.pincode} onChange={handleInputChange} required size="small" />
                </Grid>
                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                  <Button type="submit" variant="contained" startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <AddIcon />} disabled={submitting}>
                    {submitting ? 'Adding...' : 'Add Address'}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </StyledPaper>

          <StyledPaper elevation={0}>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Location List</Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <TextField size="small" placeholder="Search locations..." value={search} onChange={handleSearchChange} InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} /> }} sx={{ width: { xs: '100%', sm: 300 } }} />
                <IconButton onClick={fetchAddresses} color="primary" title="Refresh List"><RefreshIcon /></IconButton>
              </Box>
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>State</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>District</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Subdistrict</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Village</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Pincode</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3 }}><CircularProgress size={24} /></TableCell></TableRow>
                  ) : addresses.length === 0 ? (
                    <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3 }}><Typography color="text.secondary">No locations found</Typography></TableCell></TableRow>
                  ) : (
                    addresses.map((addr) => (
                      <TableRow key={addr.id} hover>
                        <TableCell>{addr.state_name} ({addr.state_code})</TableCell>
                        <TableCell>{addr.district_name}</TableCell>
                        <TableCell>{addr.subdistrict_name}</TableCell>
                        <TableCell>{addr.village_name}</TableCell>
                        <TableCell><Typography sx={{ fontWeight: 600, color: 'primary.main' }}>{addr.pincode}</Typography></TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
              <Pagination count={totalPages} page={page} onChange={(e, v) => setPage(v)} color="primary" showFirstButton showLastButton />
            </Box>
          </StyledPaper>
        </Box>
      </Fade>
    </Box>
  );
};

export default AdminAddressManagement;